import { useEffect, useMemo } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type Option = {
  value: string;
  title: string;
  description?: string;
};

const HUB_DEFAULT_VALUE = 'own_spoke';

export const DataTeamSetupField = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const isHub = formContext?.formData?.hub_spoke === 'Hub';

  const options = useMemo<Option[]>(() => {
    const fromSchema = (schema as any)?.oneOf as Array<any> | undefined;
    if (fromSchema?.length) {
      return fromSchema.map(entry => ({
        value: entry.const,
        title: entry.title ?? entry.const,
        description: entry.description,
      }));
    }

    return [
      { value: 'parent_hub', title: "Using parent hub's data team" },
      { value: 'group_data', title: 'Using Group Data team' },
      { value: 'own_spoke', title: 'Own Data team' },
      { value: 'passive', title: 'No data team (passive spoke)' },
    ];
  }, [schema]);

  useEffect(() => {
    if (isHub && formData !== HUB_DEFAULT_VALUE) {
      onChange(HUB_DEFAULT_VALUE);
    }
  }, [isHub, formData, onChange]);

  const selectedValue = isHub ? HUB_DEFAULT_VALUE : formData ?? '';
  const selectedOption = options.find(o => o.value === selectedValue);
  const hasError = Boolean(rawErrors?.length);

  return (
    <FormControl fullWidth margin="normal" required={required} error={hasError}>
      <InputLabel id="data-team-setup-select-label" shrink>
        {schema?.title ?? 'Data Team Setup'}
      </InputLabel>
      <Select
        labelId="data-team-setup-select-label"
        id="data-team-setup-select"
        value={selectedValue}
        onChange={event =>
          onChange((event.target.value as string) || undefined)
        }
        disabled={isHub}
        displayEmpty
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.title}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {hasError
          ? rawErrors?.join(', ')
          : selectedOption?.description ?? schema?.description}
      </FormHelperText>
    </FormControl>
  );
};
